import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { fehlendeFunktion } from '../billtoro/upgrade';
import { useToast } from '../lib/toast';
import { ChipRadios } from './ChipRadios';
import { KUNDEN_ARTEN, type Art } from './formular';
import { SchlagwortFeld } from './kontakte/Schlagwoerter';
import { kundenUrl, kundeUrl, tage, type KundeAntwort, type KundeVoll } from './KundeDaten';
import { EU_LAENDER } from './laender';
import { istDrittland, LAENDER_WELT } from './laender-welt';
import type { Overview } from './types';
import { WaehrungWahl } from './WaehrungFeld';
import type { Waehrung } from './waehrung';
import './kunden.css';

// Kundenart wie im Rechnungsformular (formular.ts): Firma im EU-Ausland ist eine Firma mit anderem Land, Kunde außerhalb
// der EU eine Firma oder Privatperson mit einem Land außerhalb der EU
const NUMMER_MAX = 59999;
const WELT_ARTEN: [('firma' | 'privat'), string][] = [
  ['firma', 'Firma'],
  ['privat', 'Privatperson'],
];

interface Werte {
  art: Art;
  name: string;
  extra: string;
  street: string;
  zip: string;
  city: string;
  // EU-Land (bei 'eu') bzw. Land außerhalb der EU und ob Privatperson (bei 'welt')
  country: string;
  land: string;
  weltPrivat: boolean;
  vatId: string;
  email: string;
  emailCc: string;
  phone: string;
  nummer: string;
  paymentDays: string;
  xrechnung: boolean;
  buyerRef: string;
  notiz: string;
  // nicht automatisch mahnen (Mahnlauf)
  mahnstopp: boolean;
  // Währung für neue Belege
  waehrung: Waehrung;
  // zum Ordnen und Filtern
  schlagwoerter: string[];
}

const leer: Werte = {
  art: 'firma',
  name: '',
  extra: '',
  street: '',
  zip: '',
  city: '',
  country: 'DE',
  land: 'CH',
  weltPrivat: false,
  vatId: '',
  email: '',
  emailCc: '',
  phone: '',
  nummer: '',
  paymentDays: '',
  xrechnung: false,
  buyerRef: '',
  notiz: '',
  mahnstopp: false,
  waehrung: 'EUR',
  schlagwoerter: [],
};

const werteOf = (k: KundeVoll): Werte => ({
  art: k.country === 'DE' ? k.art : istDrittland(k.country) ? 'welt' : 'eu',
  name: k.name,
  extra: k.extra ?? '',
  street: k.street,
  zip: k.zip,
  city: k.city,
  country: istDrittland(k.country) ? 'DE' : k.country,
  land: istDrittland(k.country) ? k.country : 'CH',
  weltPrivat: istDrittland(k.country) && k.art === 'privat',
  vatId: k.vatId ?? '',
  email: k.email ?? '',
  emailCc: k.emailCc ?? '',
  phone: k.phone ?? '',
  nummer: String(k.nummer),
  paymentDays: k.paymentDays == null ? '' : String(k.paymentDays),
  xrechnung: k.xrechnung,
  buyerRef: k.buyerRef ?? '',
  notiz: k.notiz ?? '',
  mahnstopp: k.mahnstopp,
  waehrung: k.waehrung ?? 'EUR',
  schlagwoerter: k.schlagwoerter ?? [],
});

// Neuer Kunde (/rechnung/kunden/neu) oder Kunde bearbeiten (/rechnung/kunden/:id/bearbeiten). Die Prüfung macht der
// Server (wie beim Kunden auf der Rechnung); gleicher Name und PLZ: erst ein Hinweis, dann auf Wunsch trotzdem.
// vorschlaege: Schlagwörter der Firma (aus der Liste) für das Feld „Schlagwörter“.
export function KundeForm({
  data,
  id,
  naechste,
  vorschlaege,
  onSaved,
}: {
  data: Overview;
  id?: string;
  naechste: number | null;
  vorschlaege: string[];
  onSaved(): Promise<void>;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [w, setW] = useState<Werte | null>(id ? null : leer);
  const [alt, setAlt] = useState<KundeAntwort | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [doppelt, setDoppelt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mehr, setMehr] = useState(false);

  useEffect(() => {
    if (!id) return;
    let weg = false;
    api
      .get<KundeAntwort>(kundeUrl(id))
      .then((r) => {
        if (weg) return;
        setAlt(r);
        setW(werteOf(r.kunde));
        const k = r.kunde;
        setMehr(k.xrechnung || k.paymentDays != null || !!k.notiz || !!k.buyerRef || k.mahnstopp || (k.waehrung ?? 'EUR') !== 'EUR');
      })
      .catch((err) => !weg && setLadeFehler(errorText(err)));
    return () => {
      weg = true;
    };
  }, [id]);

  if (ladeFehler) {
    return (
      <div className="notice bad">
        {ladeFehler} <Link to="/rechnung/kunden">Zur Kundenliste</Link>
      </div>
    );
  }
  if (!w) return <div className="spinner" aria-label="Lädt" />;

  // Vorschlag für die Nummer (vergeben wird sie beim Speichern)
  const vorschlag =
    naechste ?? Math.min(NUMMER_MAX, Math.max(0, ...data.kunden.map((k) => k.nummer ?? 0).filter((n) => n <= NUMMER_MAX)) + 1);
  // Nummer fest, sobald es Rechnungen gibt (so steht der Kunde in der Buchhaltung)
  const nummerFest = !!alt && alt.rechnungen.length > 0;
  const firma = w.art === 'firma' || w.art === 'eu' || (w.art === 'welt' && !w.weltPrivat);
  // Währung für neue Belege nur mit der Funktion im Tarif (eine schon gewählte bleibt sichtbar, um sie zurückzusetzen)
  const mitWaehrung = !fehlendeFunktion(data.tarif, 'waehrung') || w.waehrung !== 'EUR';
  const set = (v: Partial<Werte>) => {
    setW({ ...w, ...v });
    setDoppelt(null);
  };

  function pickArt(a: Art) {
    const v: Partial<Werte> = { art: a };
    if (a === 'eu' && w!.country === 'DE') v.country = 'AT';
    if (a !== 'eu') v.country = 'DE';
    // Behörden bekommen XRechnung vorgeschlagen (bei einem neuen Kunden; zurück zu Firma: wieder aus)
    if (a === 'behoerde' && !id) {
      v.xrechnung = true;
      setMehr(true);
    } else if (w!.art === 'behoerde' && !id && !w!.buyerRef) v.xrechnung = false;
    set(v);
  }

  async function save(trotzdem = false) {
    if (!w) return;
    const nummer = w.nummer.trim();
    if (nummer && !/^\d+$/.test(nummer)) return setFehler(`Bitte eine Kundennummer von 1 bis ${NUMMER_MAX} eingeben.`);
    const ziel = w.paymentDays.trim();
    if (ziel && !/^\d+$/.test(ziel)) return setFehler('Bitte das Zahlungsziel in Tagen eintragen (0 bis 90).');
    const body = {
      name: w.name,
      extra: w.extra,
      street: w.street,
      zip: w.zip,
      city: w.city,
      country: w.art === 'eu' ? w.country : w.art === 'welt' ? w.land : 'DE',
      art: w.art === 'eu' ? 'firma' : w.art === 'welt' ? (w.weltPrivat ? 'privat' : 'firma') : w.art,
      vatId: firma ? w.vatId : null,
      email: w.email,
      emailCc: w.emailCc,
      phone: w.phone,
      buyerRef: w.buyerRef,
      xrechnung: w.xrechnung,
      nummer: nummer ? Number(nummer) : null,
      paymentDays: ziel ? Number(ziel) : null,
      notiz: w.notiz,
      mahnstopp: w.mahnstopp,
      waehrung: w.waehrung,
      schlagwoerter: w.schlagwoerter,
      trotzdem,
    };
    setBusy(true);
    setFehler(null);
    setDoppelt(null);
    try {
      const res = id
        ? await api.put<{ id: string; nummer: number }>(kundeUrl(id), body)
        : await api.post<{ id: string; nummer: number }>(kundenUrl(data.firm.id), body);
      await onSaved();
      toast(id ? 'Kunde gespeichert' : `Kunde ${res.nummer} angelegt`);
      navigate(`/rechnung/kunden/${res.id}`, { replace: !!id });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'doppelt') setDoppelt(err.message);
      else setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void save();
  }

  const zurueck = id ? `/rechnung/kunden/${id}` : '/rechnung/kunden';
  return (
    <form className="rt-card kd-form" onSubmit={submit} noValidate>
      <Link to={zurueck} className="kd-back">
        ← {id ? 'Zurück zum Kunden' : 'Alle Kunden'}
      </Link>
      <h1>{id ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h1>
      {alt && alt.rechnungen.length > 0 && (
        <p className="muted kd-sub">Geschriebene Rechnungen bleiben, wie sie sind. Die Änderung gilt für neue Rechnungen.</p>
      )}

      <ChipRadios label="Kundenart" className="rt-kart" value={w.art} options={KUNDEN_ARTEN} onChange={pickArt} />
      <div className="rt-grid">
        <label className="field rt-span2">
          <span>Name oder Firma</span>
          <input value={w.name} autoComplete="off" onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Zusatz (optional)</span>
          <input value={w.extra} placeholder="z. B. z. Hd. Frau Meier" onChange={(e) => set({ extra: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Straße und Hausnummer</span>
          <input value={w.street} onChange={(e) => set({ street: e.target.value })} />
        </label>
        <label className="field">
          <span>{w.art === 'welt' ? 'PLZ (optional)' : 'PLZ'}</span>
          <input value={w.zip} inputMode={w.art === 'eu' || w.art === 'welt' ? 'text' : 'numeric'} onChange={(e) => set({ zip: e.target.value })} />
        </label>
        <label className="field">
          <span>Ort</span>
          <input value={w.city} onChange={(e) => set({ city: e.target.value })} />
        </label>
        {w.art === 'eu' && (
          <label className="field rt-span2">
            <span>Land</span>
            <select value={w.country} onChange={(e) => set({ country: e.target.value })}>
              {EU_LAENDER.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        )}
        {w.art === 'welt' && (
          <>
            <label className="field rt-span2">
              <span>Land</span>
              <select value={w.land} onChange={(e) => set({ land: e.target.value })}>
                {LAENDER_WELT.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <div className="field rt-span2">
              <ChipRadios
                label="Firma / Privatperson"
                value={w.weltPrivat ? 'privat' : 'firma'}
                options={WELT_ARTEN}
                onChange={(a) => set({ weltPrivat: a === 'privat' })}
              />
            </div>
            {!w.weltPrivat && (
              <label className="field rt-span2">
                <span>Steuernummer des Kunden (optional)</span>
                <input value={w.vatId} maxLength={20} autoComplete="off" onChange={(e) => set({ vatId: e.target.value })} />
              </label>
            )}
          </>
        )}
        {firma && w.art !== 'welt' && (
          <label className="field rt-span2">
            <span>USt-IdNr.{w.art === 'eu' ? '' : ' (optional)'}</span>
            <input
              value={w.vatId}
              autoComplete="off"
              placeholder={w.art === 'eu' ? `z. B. ${EU_LAENDER.find((l) => l[0] === w.country)?.[2] ?? ''}…` : 'z. B. DE123456789'}
              onChange={(e) => set({ vatId: e.target.value })}
            />
          </label>
        )}
      </div>

      <h2>Kontakt</h2>
      <div className="rt-grid">
        <label className="field rt-span2">
          <span>E-Mail{w.xrechnung ? '' : ' (optional)'}</span>
          <input type="email" value={w.email} autoComplete="off" placeholder="z. B. rechnung@kunde.de" onChange={(e) => set({ email: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Telefon (optional)</span>
          <input type="tel" value={w.phone} autoComplete="off" onChange={(e) => set({ phone: e.target.value })} />
        </label>
        <label className="field rt-span4">
          <span>Kopie der E-Mails an (optional)</span>
          <input
            value={w.emailCc}
            autoComplete="off"
            placeholder="z. B. buchhaltung@kunde.de"
            onChange={(e) => set({ emailCc: e.target.value })}
          />
          <small>Mehrere mit Komma trennen.</small>
        </label>
      </div>
      <SchlagwortFeld value={w.schlagwoerter} vorschlaege={vorschlaege} onChange={(v) => set({ schlagwoerter: v })} />

      <details className="kd-more" open={mehr} onToggle={(e) => setMehr((e.target as HTMLDetailsElement).open)}>
        <summary>
          Weitere Angaben <span className="muted">Kundennummer, Zahlungsziel{mitWaehrung ? ', Währung' : ''}, XRechnung, Notiz</span>
        </summary>
        <div className="rt-grid">
          <label className="field">
            <span>Kundennummer</span>
            <input
              value={w.nummer}
              inputMode="numeric"
              placeholder={id ? undefined : String(vorschlag)}
              readOnly={nummerFest}
              onChange={(e) => set({ nummer: e.target.value })}
            />
            <small>
              {nummerFest
                ? 'Fest, weil es schon Rechnungen gibt.'
                : id
                  ? `Frei wählbar von 1 bis ${NUMMER_MAX.toLocaleString('de-DE')}.`
                  : 'Leer lassen: die nächste freie Nummer.'}
            </small>
          </label>
          <label className="field">
            <span>Zahlungsziel</span>
            <span className="kd-unit">
              <input
                value={w.paymentDays}
                inputMode="numeric"
                placeholder={String(data.absender.paymentDays)}
                aria-label="Zahlungsziel in Tagen"
                onChange={(e) => set({ paymentDays: e.target.value })}
              />
              <span>Tage</span>
            </span>
            <small>Leer lassen: {tage(data.absender.paymentDays)} wie in Ihren Angaben.</small>
          </label>
          {mitWaehrung && (
            <div className="kd-waehrung rt-span2">
              <WaehrungWahl label="Währung für neue Belege" value={w.waehrung} tarif={data.tarif} onChange={(v) => set({ waehrung: v })} />
              <small className="muted">Jedes neue Formular für diesen Kunden startet damit.</small>
            </div>
          )}
        </div>

        <label className="check">
          <input type="checkbox" checked={w.xrechnung} onChange={(e) => set({ xrechnung: e.target.checked })} />
          <span>Rechnungen als XRechnung (für Behörden und öffentliche Auftraggeber)</span>
        </label>
        {w.xrechnung && (
          <div className="rt-grid kd-sub-grid">
            <label className="field rt-span2">
              <span>Leitweg-ID</span>
              <input value={w.buyerRef} placeholder="z. B. 04011000-1234512345-06" onChange={(e) => set({ buyerRef: e.target.value })} />
              <small>Steht im Auftrag der Behörde. Die E-Mail oben ist dann Pflicht.</small>
            </label>
          </div>
        )}
        <label className="field">
          <span>Notiz (nur für Sie)</span>
          <textarea value={w.notiz} maxLength={1000} placeholder="z. B. Rechnungen immer mit Auftragsnummer" onChange={(e) => set({ notiz: e.target.value })} />
        </label>
        {/* Mahnläufe (Billtoro: nur im Tarif damit) */}
        {!fehlendeFunktion(data.tarif, 'mahnlauf') && (
          <>
            <label className="check kd-mahnstopp">
              <input type="checkbox" checked={w.mahnstopp} onChange={(e) => set({ mahnstopp: e.target.checked })} />
              <span>Nicht automatisch mahnen</span>
            </label>
            <p className="rt-hint kd-mahnstopp-h">Mahnungen an diesen Kunden nur von Hand.</p>
          </>
        )}
      </details>

      {fehler && <div className="notice bad">{fehler}</div>}
      {doppelt && (
        <div className="notice warn kd-doppelt">
          <span>{doppelt}</span>
          <button type="button" className="btn small" disabled={busy} onClick={() => void save(true)}>
            Trotzdem speichern
          </button>
        </div>
      )}
      <div className="rt-actions">
        <Link to={zurueck} className="btn">
          Abbrechen
        </Link>
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? 'Wird gespeichert …' : id ? 'Speichern' : 'Kunde anlegen'}
        </button>
      </div>
    </form>
  );
}
